import React, { lazy, Suspense } from "react";
import { LoaderCircle } from "lucide-react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import POS from "@/pages/POS";
import ServicesPOS from "@/pages/ServicesPOS";

import "@/App.css";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import RoleGuard from "@/components/RoleGuard";
import AppLayout from "@/components/layout/AppLayout";
import PwaInstallPrompt from "@/components/PwaInstallPrompt";

// Public pages
const Home = lazy(() => import("@/pages/Home"));
const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy"));
const Login = lazy(() => import("@/pages/Login"));
const Register = lazy(() => import("@/pages/Register"));
const AuthCallback = lazy(() => import("@/pages/AuthCallback"));

// Protected application pages
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const ChartOfAccounts = lazy(() => import("@/pages/ChartOfAccounts"));
const JournalEntries = lazy(() => import("@/pages/JournalEntries"));
const GeneralLedger = lazy(() => import("@/pages/GeneralLedger"));
const Cashbook = lazy(() => import("@/pages/Cashbook"));
const SalesInvoicing = lazy(() => import("@/pages/SalesInvoicing"));
const SalesHistory = lazy(() => import("@/pages/SalesHistory"));
const InventoryMovements = lazy(() => import("@/pages/InventoryMovements"));
const AccountsPayable = lazy(() => import("@/pages/AccountsPayable"));
const Inventory = lazy(() => import("@/pages/Inventory"));
const Payroll = lazy(() => import("@/pages/Payroll"));
const Reports = lazy(() => import("@/pages/Reports"));
const AccountingControls = lazy(() => import("@/pages/AccountingControls"));
const ReportsCenter = lazy(() => import("@/pages/ReportsCenter"));
const FixedAssets = lazy(() => import("@/pages/FixedAssets"));
const BankReconciliation = lazy(() => import("@/pages/BankReconciliation"));
const BankAccounts = lazy(() => import("@/pages/BankAccounts"));
const AuditLog = lazy(() => import("@/pages/AuditLog"));
const Settings = lazy(() => import("@/pages/Settings"));
const SuperAdmin = lazy(() => import("@/pages/SuperAdmin"));
const Boardroom = lazy(() => import("@/pages/Boardroom"));
const Analytics = lazy(() => import("@/pages/Analytics"));
const PettyCash = lazy(() => import("@/pages/PettyCash"));
const PurchaseOrders = lazy(() => import("@/pages/PurchaseOrders"));
const Suppliers = lazy(() => import("@/pages/Suppliers"));
const Budgeting = lazy(() => import("@/pages/Budgeting"));
const Productions = lazy(() => import("@/pages/Productions"));
const HospitalManagement = lazy(() => import("@/pages/HospitalManagement"));
const HospitalPharmacy = lazy(() => import("@/pages/HospitalPharmacy"));
const PharmacyInventory = lazy(() => import("@/pages/PharmacyInventory"));
const HospitalClinicalUnits = lazy(() => import("@/pages/HospitalClinicalUnits"));
const SchoolManagement = lazy(() => import("@/pages/SchoolManagement"));
const SchoolSite = lazy(() => import("@/pages/SchoolSite"));
const HospitalWorkflow = lazy(() => import("@/pages/HospitalWorkflow"));
const ParentPortal = lazy(() => import("@/pages/ParentPortal"));
const Microfinance = lazy(() => import("@/pages/Microfinance"));
const MfiClientProfile = lazy(() => import("@/components/mfi/MfiClientProfile").then((module) => ({ default: module.MfiClientProfile })));
const FieldApp = lazy(() => import("@/pages/FieldApp"));
const SalespersonPortal = lazy(() => import("@/pages/SalespersonPortal"));
const Subscribe = lazy(() => import("@/pages/Subscribe"));
const PaymentCallback = lazy(() => import("@/pages/PaymentCallback"));

// Office Administration suite
const OfficeDocuments = lazy(() => import("@/pages/office/Documents"));
const OfficeMail = lazy(() => import("@/pages/office/Mail"));
const OfficeChat = lazy(() => import("@/pages/office/Chat"));
const OfficeCalendar = lazy(() => import("@/pages/office/Calendar"));
const OfficeCRM = lazy(() => import("@/pages/office/CRM"));
const OfficeMemos = lazy(() => import("@/pages/office/Memos"));
const OfficeJobs = lazy(() => import("@/pages/office/Jobs"));
const OfficePeople = lazy(() => import("@/pages/office/People"));


function AppRouter() {
  const location = useLocation();

  /*
   * OAuth callback handling.
   *
   * Some OAuth providers return the session_id in the URL fragment.
   * Handle that callback regardless of which pathname the provider
   * redirects to.
   */
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* ============================================================
          PUBLIC ROUTES
      ============================================================ */}

      <Route path="/" element={<Home />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/payment/callback" element={<PaymentCallback />} />
      <Route path="/school-site/:slug/*" element={<SchoolSite />} />
      <Route path="/parent/*" element={<ParentPortal />} />
      <Route path="/field" element={<RoleGuard><FieldApp /></RoleGuard>} />
      <Route
        path="/auth/callback"
        element={<AuthCallback />}
      />

      {/* ============================================================
          PROTECTED APPLICATION ROUTES
      ============================================================ */}

      <Route
        element={
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        <Route
          path="/dashboard"
          element={
            <RoleGuard>
              <Dashboard />
            </RoleGuard>
          }
        />

        <Route
          path="/boardroom"
          element={
            <RoleGuard>
              <Boardroom />
            </RoleGuard>
          }
        />

        <Route
          path="/analytics"
          element={
            <RoleGuard>
              <Analytics />
            </RoleGuard>
          }
        />

        <Route
          path="/petty-cash"
          element={
            <RoleGuard>
              <PettyCash />
            </RoleGuard>
          }
        />

        <Route
          path="/accounts"
          element={
            <RoleGuard>
              <ChartOfAccounts />
            </RoleGuard>
          }
        />

        <Route
          path="/journals"
          element={
            <RoleGuard>
              <JournalEntries />
            </RoleGuard>
          }
        />

        <Route
          path="/ledger"
          element={
            <RoleGuard>
              <GeneralLedger />
            </RoleGuard>
          }
        />

        <Route
          path="/cashbook"
          element={
            <RoleGuard>
              <Cashbook />
            </RoleGuard>
          }
        />

        <Route
          path="/invoicing"
          element={
            <RoleGuard>
              <SalesInvoicing />
            </RoleGuard>
          }
        />

        <Route
          path="/pos"
          element={
            <RoleGuard>
              <POS />
            </RoleGuard>
          }
        />

        <Route
          path="/services-pos"
          element={
            <RoleGuard>
              <ServicesPOS />
            </RoleGuard>
          }
        />

        <Route
          path="/productions"
          element={
            <RoleGuard>
              <Productions />
            </RoleGuard>
          }
        />

        <Route path="/hospital" element={<RoleGuard><HospitalManagement /></RoleGuard>} />
        <Route path="/hospital/pharmacy" element={<RoleGuard><HospitalPharmacy /></RoleGuard>} />
        <Route path="/hospital/pharmacy/inventory" element={<RoleGuard><PharmacyInventory /></RoleGuard>} />
        <Route path="/pharmacy" element={<RoleGuard><HospitalPharmacy standalone /></RoleGuard>} />
        <Route path="/pharmacy/inventory" element={<RoleGuard><PharmacyInventory /></RoleGuard>} />
        <Route path="/pharmacy/products" element={<RoleGuard><PharmacyInventory /></RoleGuard>} />
        <Route path="/pharmacy/procurement" element={<RoleGuard><PharmacyInventory /></RoleGuard>} />
        <Route path="/pharmacy/receipts" element={<RoleGuard><PharmacyInventory /></RoleGuard>} />
        <Route path="/pharmacy/adjustments" element={<RoleGuard><PharmacyInventory /></RoleGuard>} />
        <Route path="/pharmacy/movements" element={<RoleGuard><PharmacyInventory /></RoleGuard>} />
        <Route path="/pharmacy/pos" element={<RoleGuard><POS mode="pharmacy" /></RoleGuard>} />
        {["/hospital/desk", "/hospital/diagnostics", "/hospital/referrals", "/hospital/follow-ups", "/hospital/ward-care", "/pharmacy/desk"].map(p => <Route key={p} path={p} element={<RoleGuard><HospitalWorkflow /></RoleGuard>} />)}
        <Route path="/hospital/units/:unit" element={<RoleGuard><HospitalClinicalUnits /></RoleGuard>} />
        <Route path="/hospital/:section" element={<RoleGuard><HospitalManagement /></RoleGuard>} />
        <Route path="/school" element={<RoleGuard><SchoolManagement /></RoleGuard>} />
        <Route path="/school/:section" element={<RoleGuard><SchoolManagement /></RoleGuard>} />
        <Route path="/microfinance" element={<RoleGuard><Microfinance /></RoleGuard>} />
        <Route path="/microfinance/clients/:clientId" element={<RoleGuard><MfiClientProfile /></RoleGuard>} />
        <Route path="/microfinance/:section" element={<RoleGuard><Microfinance /></RoleGuard>} />

        <Route
          path="/productions/:tab"
          element={
            <RoleGuard>
              <Productions />
            </RoleGuard>
          }
        />

        <Route
          path="/productions/salesperson/:id"
          element={
            <RoleGuard>
              <SalespersonPortal />
            </RoleGuard>
          }
        />

        <Route
          path="/sales-history"
          element={
            <RoleGuard>
              <SalesHistory />
            </RoleGuard>
          }
        />

        <Route
          path="/payables"
          element={
            <RoleGuard>
              <AccountsPayable />
            </RoleGuard>
          }
        />

        <Route
          path="/purchase-orders"
          element={
            <RoleGuard>
              <PurchaseOrders />
            </RoleGuard>
          }
        />

        <Route
          path="/customers"
          element={
            <RoleGuard>
              <Suppliers />
            </RoleGuard>
          }
        />

        <Route
          path="/suppliers"
          element={
            <RoleGuard>
              <Suppliers />
            </RoleGuard>
          }
        />

        <Route
          path="/budgeting"
          element={
            <RoleGuard>
              <Budgeting />
            </RoleGuard>
          }
        />

        <Route
          path="/inventory"
          element={
            <RoleGuard>
              <Inventory />
            </RoleGuard>
          }
        />

        <Route
          path="/inventory-movements"
          element={
            <RoleGuard>
              <InventoryMovements />
            </RoleGuard>
          }
        />

        <Route
          path="/fixed-assets"
          element={
            <RoleGuard>
              <FixedAssets />
            </RoleGuard>
          }
        />

        <Route
          path="/banks"
          element={
            <RoleGuard>
              <BankAccounts />
            </RoleGuard>
          }
        />

        <Route
          path="/bank-recon"
          element={
            <RoleGuard>
              <BankReconciliation />
            </RoleGuard>
          }
        />

        <Route
          path="/payroll"
          element={
            <RoleGuard>
              <Payroll />
            </RoleGuard>
          }
        />

        <Route
          path="/reports/controls"
          element={
            <RoleGuard>
              <AccountingControls />
            </RoleGuard>
          }
        />

        <Route
          path="/reports"
          element={
            <RoleGuard>
              <Reports />
            </RoleGuard>
          }
        />

        <Route
          path="/reports-center"
          element={
            <RoleGuard>
              <ReportsCenter />
            </RoleGuard>
          }
        />

        <Route
          path="/audit-log"
          element={
            <RoleGuard>
              <AuditLog />
            </RoleGuard>
          }
        />

        <Route
          path="/settings"
          element={
            <RoleGuard>
              <Settings />
            </RoleGuard>
          }
        />

        <Route
          path="/admin"
          element={
            <RoleGuard>
              <SuperAdmin />
            </RoleGuard>
          }
        />

        {/* ========================================================
            OFFICE ADMINISTRATION SUITE
        ======================================================== */}
        <Route path="/office/documents" element={<RoleGuard><OfficeDocuments /></RoleGuard>} />
        <Route path="/office/mail" element={<RoleGuard><OfficeMail /></RoleGuard>} />
        <Route path="/office/chat" element={<RoleGuard><OfficeChat /></RoleGuard>} />
        <Route path="/office/calendar" element={<RoleGuard><OfficeCalendar /></RoleGuard>} />
        <Route path="/office/crm" element={<RoleGuard><OfficeCRM /></RoleGuard>} />
        <Route path="/office/memos" element={<RoleGuard><OfficeMemos /></RoleGuard>} />
        <Route path="/office/jobs" element={<RoleGuard><OfficeJobs /></RoleGuard>} />
        <Route path="/office/people" element={<RoleGuard><OfficePeople /></RoleGuard>} />

        {/* Subscription (accessible to any authenticated workspace user) */}
        <Route path="/subscribe" element={<RoleGuard><Subscribe /></RoleGuard>} />
      </Route>

      {/* ============================================================
          FALLBACK
      ============================================================ */}

      <Route
        path="*"
        element={<Navigate to="/" replace />}
      />
    </Routes>
  );
}


export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppErrorBoundary>
            <Suspense fallback={<AppPageLoading />}>
              <AppRouter />
            </Suspense>
          </AppErrorBoundary>
          <PwaInstallPrompt />
          <Toaster
            richColors
            position="top-right"
          />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

class AppErrorBoundary extends React.Component {
  state = { error: null };

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error) {
    const message = `${error?.name || ""} ${error?.message || ""}`;
    if (!/ChunkLoadError|Loading chunk|dynamically imported module|module script failed/i.test(message)) return;
    try {
      if (!sessionStorage.getItem("egs-chunk-reload-attempted")) {
        sessionStorage.setItem("egs-chunk-reload-attempted", "1");
        window.location.reload();
      }
    } catch { /* use the manual reload screen if storage is unavailable */ }
  }

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-slate-950 px-6 text-center text-white" role="alert">
        <h1 className="text-lg font-semibold">This page could not be loaded</h1>
        <p className="max-w-md text-sm text-slate-300">The app may have updated while this tab was open. Reload to get the latest files.</p>
        <button type="button" onClick={() => { sessionStorage.removeItem("egs-chunk-reload-attempted"); window.location.reload(); }} className="min-h-11 rounded-lg bg-emerald-500 px-4 font-semibold text-slate-950">Reload app</button>
      </div>
    );
  }
}

function AppPageLoading() {
  return (
    <div className="app-page-loading" role="status" aria-live="polite" aria-busy="true" data-testid="app-page-loading">
      <div className="app-page-loading__content">
        <div className="app-page-loading__brand">
          <img src="/branding/egs-logo.jpg" alt="" />
          <span>EGS Business Suite</span>
        </div>
        <div className="app-page-loading__message">
          <LoaderCircle className="h-5 w-5 animate-spin" />
          <span>Opening your workspace</span>
        </div>
        <div className="app-page-loading__track" aria-hidden="true">
          <div className="app-page-loading__progress" />
        </div>
        <div className="app-page-loading__skeleton" aria-hidden="true">
          <div />
          <div />
          <div />
        </div>
      </div>
    </div>
  );
}
